// components/portal/FinancialsView.tsx
'use client';

import React, { useCallback, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ChartErrorBoundary } from '@/components/ui/ChartErrorBoundary';
import { LeasingBrandFinancialRecordDTO, LeasingLegalEntityDTO } from '#types/types';
import { AlertCircle, Fingerprint } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';
import { FranceOnlyChip } from '@/components/ui/FranceOnlyChip';

const PANEL_FONT = 'ui-sans-serif, -apple-system, system-ui, sans-serif';

interface FinancialsViewProps {
  legalEntity: LeasingLegalEntityDTO | null;
  financials: LeasingBrandFinancialRecordDTO[];
}

const translations = {
  en: {
    margin: 'margin',
    revenue: 'Revenue',
    netIncome: 'Net Income',
    noFinancialData: 'No financial data or legal identity linked.',
    unknown: 'Unknown',
    healthScore: 'Financial health score',
    noHistoricalData: 'No historical data available for visualization.',
  },
  fr: {
    margin: 'marge',
    revenue: 'Chiffre d\'affaires',
    netIncome: 'Resultat net',
    noFinancialData: 'Aucune donnee financiere ou identite legale liee.',
    unknown: 'Inconnu',
    healthScore: 'Score de sante financiere',
    noHistoricalData: 'Aucune donnee historique pour la visualisation.',
  },
};

type FinancialsTranslations = typeof translations.en;

const formatCurrency = (val: number) => {
  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (abs >= 1000000000) return `${sign}€${(abs / 1000000000).toFixed(1)}B`;
  if (abs >= 1000000) return `${sign}€${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}€${(abs / 1000).toFixed(0)}k`;
  return `${sign}€${abs}`;
};

const CHART_THEME = {
  axis: {
    ticks: { text: { fill: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: PANEL_FONT } },
    domain: { line: { stroke: 'transparent', strokeWidth: 0 } },
  },
  grid: { line: { stroke: '#2A2A2A', strokeDasharray: '4 4', strokeWidth: 1 } },
  tooltip: { container: { background: '#000', color: '#fff' } },
  legends: {
      text: {
          fontSize: 12,
          fill: 'rgba(255,255,255,0.5)',
          fontFamily: PANEL_FONT,
          fontWeight: 500
      }
  }
};

const BAR_KEYS = ['Revenue'];
const BAR_MARGIN = { top: 30, right: 0, bottom: 40, left: 60 };
const BAR_INDEX_SCALE = { type: 'band' as const, round: true };
const BAR_COLORS = ['rgba(255,255,255,0.85)'];
const BAR_AXIS_LEFT = {
    tickSize: 0,
    tickPadding: 12,
    format: (v: string | number) => formatCurrency(Number(v))
};
const BAR_AXIS_BOTTOM = {
    tickSize: 0,
    tickPadding: 12,
    tickRotation: -45,
};
const BAR_MARKERS = [
    {
        axis: 'y' as const,
        value: 0,
        lineStyle: { stroke: 'rgba(255, 255, 255, 0.15)', strokeWidth: 1 },
    },
];

const LINE_MARGIN = { top: 35, right: 20, bottom: 40, left: 70 };
const LINE_X_SCALE = { type: 'point' as const };
const LINE_COLORS = ['#3B82F6'];

const CustomTooltip = ({ id, value, data, labels }: { id: string | number; value: number; data: any; labels: FinancialsTranslations }) => (
  <div className="bg-[#0f0f0f]/95 backdrop-blur border border-[#2A2A2A] p-3 rounded-lg shadow-2xl text-xs min-w-[160px] z-50" style={{ fontFamily: PANEL_FONT }}>
    <div className="text-white/35 mb-2 border-b border-[#2A2A2A] pb-1 flex justify-between">
        <span>{data.year}</span>
        {data.netIncome && data.Revenue ? (
            <span className={data.netIncome > 0 ? 'text-white/70' : 'text-white/40'}>
                {((data.netIncome / data.Revenue) * 100).toFixed(1)}% {labels.margin}
            </span>
        ) : null}
    </div>
    <div className="flex items-center justify-between gap-4 mb-1.5">
      <span className="text-white/60 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-white/85" />
        {labels.revenue}
      </span>
      <span className="font-medium text-white">{formatCurrency(value)}</span>
    </div>
    {String(id) === 'Revenue' && data.netIncome !== undefined && (
       <div className="flex items-center justify-between gap-4">
         <span className="text-white/60 flex items-center gap-2">
           <div className="w-2 h-2 rounded-full bg-[#3B82F6]" />
           {labels.netIncome}
         </span>
         <span className={`font-medium ${data.netIncome >= 0 ? 'text-white/80' : 'text-white/40'}`}>
            {formatCurrency(data.netIncome)}
         </span>
       </div>
    )}
  </div>
);

export const FinancialsView = React.memo(({ legalEntity, financials }: FinancialsViewProps) => {
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];

  const chartData = useMemo(() => {
    return [...financials]
      .sort((a, b) => a.year - b.year)
      .map(f => ({
        year: f.year.toString(),
        Revenue: f.revenue ?? 0,
        netIncome: f.netIncome ?? 0,
      }));
  }, [financials]);

  const lineData = useMemo(() => [
    {
      id: 'Net Income',
      data: chartData.map(d => ({ x: d.year, y: d.netIncome }))
    }
  ], [chartData]);

  const { minValue, maxValue } = useMemo(() => {
      if (chartData.length === 0) return { minValue: 0, maxValue: 'auto' as const };

      const allValues = chartData.flatMap(d => [d.Revenue, d.netIncome]);
      const maxVal = Math.max(...allValues);
      const minVal = Math.min(...allValues);

      const calculatedMin = minVal < 0 ? minVal * 1.1 : 0;

      return {
          minValue: calculatedMin,
          maxValue: maxVal * 1.1
      };
  }, [chartData]);

  const sortedByYearDesc = useMemo(() => [...financials].sort((a, b) => b.year - a.year), [financials]);
  const latestRecord = sortedByYearDesc[0];

  const score = legalEntity?.financialScore;

  const barValueScale = useMemo(() => ({ type: 'linear' as const, min: minValue, max: maxValue }), [minValue, maxValue]);
  const lineYScale = useMemo(() => ({ type: 'linear' as const, min: minValue, max: maxValue, stacked: false, reverse: false }), [minValue, maxValue]);

  const barLegends = useMemo(() => [
    {
        dataFrom: 'keys' as const,
        data: [
            { id: 'Revenue', label: t.revenue, color: 'rgba(255,255,255,0.85)' },
            { id: 'Net Income', label: t.netIncome, color: '#3B82F6' }
        ],
        anchor: 'top-right' as const,
        direction: 'row' as const,
        justify: false,
        translateX: 0,
        translateY: -30,
        itemsSpacing: 10,
        itemWidth: 100,
        itemHeight: 20,
        itemDirection: 'left-to-right' as const,
        itemOpacity: 1,
        symbolSize: 8,
        symbolShape: 'circle' as const
    }
  ], [t]);

  const renderBarTooltip = useCallback((props: { id: string | number; value: number; data: any }) => <CustomTooltip {...props} labels={t} />, [t]);

  if (!latestRecord && !legalEntity?.registrationNumber) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-white/20 gap-4" style={{ fontFamily: PANEL_FONT }}>
        <AlertCircle size={32} />
        <p className="text-sm">{t.noFinancialData}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#141414] rounded-xl border border-[#2A2A2A] overflow-hidden" style={{ fontFamily: PANEL_FONT }}>

      {/* Header */}
      <div className="px-3 py-2 border-b border-[#2A2A2A] flex items-center justify-between gap-2 text-[10px]">

        {/* Left: Identity */}
        <div className="flex items-center gap-2 min-w-0 overflow-hidden">
             {/* The SIREN is a fact about the brand you are reading, not a link into the legal
                 entities table. It used to open that table, which is a door out of the shortlist. */}
             <div className="flex items-center gap-1.5 text-white/70 shrink-0">
                <Fingerprint size={12} className="text-white/40 shrink-0" />
                <span>{legalEntity?.registrationNumber || t.unknown}</span>
             </div>

             {legalEntity?.country === 'FR' ? <FranceOnlyChip lang={lang} /> : null}

             {legalEntity?.legalName && (
               <>
                 <span className="text-white/10 shrink-0">|</span>
                 <div className="text-white/40 truncate max-w-[80px] sm:max-w-[120px]" title={legalEntity.legalName}>
                    {legalEntity.legalName}
                 </div>
               </>
             )}
        </div>

        {/* Right: Score */}
        <div className="flex items-center gap-2 shrink-0 bg-[#1A1A1A] rounded-lg px-2 py-1 border border-[#2A2A2A]">
            <div className="flex items-center gap-1.5" title={`${t.healthScore}: ${score ?? '-'}/100`}>
                <span className="text-[10px] text-white/30">Score</span>
                <span className="font-semibold text-xs text-white/80">{score ?? '-'}</span>
            </div>
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0 relative p-4">
         {chartData.length > 0 ? (
             <ChartErrorBoundary label="Financials">
             <div className="w-full h-full relative">
                {/* Bar layer (Revenue) */}
                <div className="absolute inset-0 z-10">
                    <ResponsiveBar
                        data={chartData}
                        keys={BAR_KEYS}
                        indexBy="year"
                        margin={BAR_MARGIN}
                        padding={0.5}
                        valueScale={barValueScale}
                        indexScale={BAR_INDEX_SCALE}
                        colors={BAR_COLORS}
                        borderRadius={3}
                        theme={CHART_THEME}
                        axisLeft={BAR_AXIS_LEFT}
                        axisBottom={BAR_AXIS_BOTTOM}
                        enableLabel={false}
                        enableGridY={true}
                        gridYValues={5}
                        markers={BAR_MARKERS}
                        tooltip={renderBarTooltip}
                        legends={barLegends}
                    />
                </div>

                {/* Line layer (Net Income) */}
                <div className="absolute inset-0 z-20 pointer-events-none">
                     <ResponsiveLine
                        data={lineData}
                        margin={LINE_MARGIN}
                        xScale={LINE_X_SCALE}
                        yScale={lineYScale}
                        curve="monotoneX"
                        colors={LINE_COLORS}
                        lineWidth={2}
                        enablePoints={true}
                        pointSize={5}
                        pointColor="#3B82F6"
                        pointBorderWidth={0}
                        pointBorderColor="#3B82F6"
                        enableGridX={false}
                        enableGridY={false}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={null}
                        axisLeft={null}
                        enableArea={false}
                        isInteractive={false}
                     />
                </div>
             </div>
             </ChartErrorBoundary>
         ) : (
             <div className="w-full h-full flex items-center justify-center text-white/20 text-sm">
                 {t.noHistoricalData}
             </div>
         )}
      </div>
    </div>
  );
});

FinancialsView.displayName = 'FinancialsView';

